import { useCallback, useEffect, useState } from "react";
import * as L from 'leaflet';
import { defaultMapConfig, useMapConfig } from "../../../../SubjectsMap/helper";

const locationIcon = '/assets/images/dashboard/project/mine-site-icon.svg';
const townIcon = '/assets/images/dashboard/project/town-icon.svg';
const villageIcon = '/assets/images/dashboard/project/location-icon.svg';
const townDotIcon = '/assets/images/dashboard/scl-icons/town-dot.svg';

const getCenter = (scl, town) => [
  ((scl.gps.latitude || 0) + (town.gps.latitude || 0)) / 2,
  ((scl.gps.longitude || 0) + (town.gps.longitude || 0)) / 2,
];

const removeFiveToCoord = (coordinates = { lat: 0, lng: 0 }) => ({
  lat: coordinates.lat,
  lng: coordinates.lng,
});

const getCoords = (gps) => [
  gps.latitude,
  gps.longitude,
];

const getBounds = (all = []) => {
  const bounds = [];

  all.forEach((item) => {
    if (item && item.gps)
      bounds.push(removeFiveToCoord(
        {
          lat: item.gps.latitude,
          lng: item.gps.longitude,
        }
      ));
  })

  return bounds;
};

const notEqualBounds = (first, last) => {
  let isValid = true;
  first.forEach(({ lat, lng }, i) => {
    const l = last[i];
    if (l) {
      if (lat !== l.lat || lng !== l.lng) {
        isValid = false;
        return;
      }
    } else {
      isValid = false;
      return;
    }
  });
  return !isValid;
};

const getTooltipContent = ({ data, t = (s) => s }) => `
  <div class="custom-tooltip">
    <div class="item">
      <h5 class="main">${t('Distance')}</h5>
      <h5>${data.distance || '-'}</h5>
    </div>
    <div class="item">
      <h5 class="main">${t('Time')}</h5>
      <h5>${data.time || '-'}</h5>
    </div>
  </div>
`;

const createTooltip = ({
  distance,
  time,
  t = (s) => s,
}) => {
  const content = getTooltipContent({ data: { distance, time }, t });
  const to = L.tooltip().setContent(content);
  return to;
};

const useMapHelper = ({
  data: _data = [],
  container,
  t,
}) => {
  const { TILE_LAYER_URL, MAP_TOKEN } = useMapConfig();
  const [mapRef, setMapRef] = useState();
  const [lastBounds, setLastBound] = useState(false);
  const [activeMarker, setActiveMarker] = useState();
  const [showSider, setShowSider] = useState(false);

  useEffect(() => {
    if (!showSider && activeMarker) {
      setShowSider(true);
    }
  }, [activeMarker]);

  // Map control handlers
  const zoomHandler = useCallback(() => {
    if (mapRef) {
      mapRef.setZoom(mapRef.getZoom() + 1);
    }
  }, [mapRef]);

  const zoomOutHandler = useCallback(() => {
    if (mapRef) {
      mapRef.setZoom(mapRef.getZoom() - 1)
    }
  }, [mapRef]);

  const moveUpHandler = useCallback(() => {
    if (mapRef) {
      const center = mapRef.getCenter();
      const value = 30 / mapRef.getZoom();
      mapRef.panTo(new L.LatLng(center.lat + value, center.lng));
    }
  }, [mapRef]);

  const moveDownHandler = useCallback(() => {
    if (mapRef) {
      const center = mapRef.getCenter();
      const value = 30 / mapRef.getZoom();
      mapRef.panTo(new L.LatLng(center.lat - value, center.lng));
    }
  }, [mapRef]);

  const removeMarkers = useCallback(() => {
    mapRef.eachLayer((layer) => {
      if (!layer._url && !layer.options.access_token) {
        mapRef.removeLayer(layer);
      }
    });
  }, [mapRef]);

  useEffect(() => {
    if (mapRef && _data) {
      const bounds = [];
      _data.forEach((data) => {
        const {
          towns = [],
          mineSites = [],
          associatedVillage = {},
        } = data;
        let _bounds = getBounds([associatedVillage, ...towns, ...mineSites]);
        bounds.push(..._bounds);
      })
      if (bounds.length > 1 && notEqualBounds(bounds, lastBounds)) {
        mapRef.fitBounds(bounds);
        setLastBound(bounds);
        zoomOutHandler();
      } else if (bounds.length === 1) {
        mapRef.setView(bounds[0], 4);
        setLastBound(bounds);
      }
    }
  }, [mapRef, _data]);

  const createInstance = () => {
    if (container.current) {
      var c = L.DomUtil.get(document.getElementById(container.current));
      if (c != null) {
        c._leaflet_id = null;
      }
      const map = L.map(container.current, {
        ...defaultMapConfig,
        center: [0, 0],
        zoom: 3,
      });
      map.addEventListener('click', () => setShowSider(false));
      L.control.scale().addTo(map);
      L.tileLayer(TILE_LAYER_URL, { access_token: MAP_TOKEN }).addTo(map);
      return map;
    }
  };

  useEffect(() => {
    if (!mapRef) {
      setMapRef(createInstance());
    }
  }, []);

  const addItems = useCallback(() => {
    if (!mapRef) {
      return;
    }
    removeMarkers();
    _data.forEach((data) => {
      const hasVillage = data.associatedVillage ? !!data.associatedVillage.gps : false;

      if (hasVillage) {
        const _m = L.marker(getCoords(data.associatedVillage.gps), {
          icon: L.icon({
            iconUrl: villageIcon,
            iconSize: [50, 50],
            zIndexOffset: 99,
          })
        }).addTo(mapRef);

        _m.addEventListener('click', () => setActiveMarker({
          data: data.associatedVillage,
          type: 'associatedVillage',
        }))
      }

      if (Array.isArray(data.towns)) {
        data.towns.forEach((item) => {
          const _m = L.marker(getCoords(item.gps), {
            icon: L.icon({
              iconUrl: townIcon,
              iconSize: [50, 50],
              zIndexOffset: 99,
            })
          }).addTo(mapRef);

          if (hasVillage) {
            const center = getCenter(data.associatedVillage, item);
            const dotM = L.marker(center, {
              icon: L.icon({
                iconUrl: townDotIcon,
                iconSize: [12, 12],
              })
            })
              .addTo(mapRef);
            const _t = createTooltip({
              distance: item.distacneToVillage,
              time: item.accessTimeToVillage,
              t,
            });
            dotM.bindTooltip(_t);
          }

          _m.addEventListener('click', () => setActiveMarker({
            data: item,
            type: 'town',
          }))

          if (hasVillage) {
            L.polyline(
              [getCoords(data.associatedVillage.gps), getCoords(item.gps)],
              { color: '#3B7C0F', weight: 2, dashArray: '4, 4', dashOffset: '4' }
            )
              .addTo(mapRef);
          }

        });
      }

      if (Array.isArray(data.mineSites)) {
        data.mineSites.forEach((item) => {
          const _m = L.marker(getCoords(item.gps), {
            icon: L.icon({
              iconUrl: locationIcon,
              iconSize: [50, 50],
              zIndexOffset: 99,
            })
          }).addTo(mapRef);

          if (hasVillage) {
            const center = getCenter(data.associatedVillage, item);
            const dotM = L.marker(center, {
              icon: L.icon({
                iconUrl: townDotIcon,
                iconSize: [12, 12],
              })
            })
              .addTo(mapRef);
            const _t = createTooltip({
              distance: item.distacneToVillage,
              time: item.accessTimeToVillage,
              t,
            });
            dotM.bindTooltip(_t);
          }

          _m.addEventListener('click', () => setActiveMarker({
            data: item,
            type: 'mineSite',
          }))

          if (hasVillage) {
            L.polyline(
              [getCoords(data.associatedVillage.gps), getCoords(item.gps)],
              { color: '#6941C6', weight: 2, dashArray: '4, 4', dashOffset: '4' }
            )
              .addTo(mapRef);
          }

        });
      }


    })
  }, [mapRef, removeMarkers, _data])

  useEffect(() => {
    addItems();
  }, [addItems]);

  return {
    showSider,
    zoomHandler,
    activeMarker,
    moveUpHandler,
    zoomOutHandler,
    moveDownHandler,
  };
};

export const getTitle = (t, type) => {
  switch (type) {
    case 'associatedVillage':
      return t('Associated Village');
    case 'mineSite':
      return t('Mine Site');
    case 'town':
      return t('Main Associated Export Town');
    default:
      return '';
  }
}

export default useMapHelper;
